import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import sanitizeHtml from 'sanitize-html';
import ContentEditable from 'react-contenteditable';
import { Radio, Button, Modal, Slider } from 'antd';
import { isLight } from '../../../modules/colorHelper';
import { extraColors } from '../../../constants/extraColors';
import { EditorElementPopover } from '../EditorElementPopover';
import {
  DeleteOutlined,
} from '@ant-design/icons';

const RadioButton = Radio.Button;
const RadioGroup = Radio.Group;


export class PText extends React.Component {
  constructor(props) {
    super(props);
    this.popoverRef = React.createRef();
  }

  state = {
    showBtn: false,
    html: this.props.text,
  };

  static propTypes = {
    size: PropTypes.string, // h1 -> h4, <small medium large> subheader,<small medium large> text
    color: PropTypes.string, // primary, accent(1-3), black-hi, black-md, black-lo, white-hi, white-md, white-lo
    shadow: PropTypes.string, // none (default), soft, hard
    text: PropTypes.string,
    mb: PropTypes.number, // margin-bottom range [1-5]
    className: PropTypes.string,
    style: PropTypes.object,

    colorScheme: PropTypes.object,
    fontScheme: PropTypes.object,
    updateNode: PropTypes.func,
    address: PropTypes.array,
    deleteElement: PropTypes.func,
    publish: PropTypes.bool,
  };

  static defaultProps = {
    size: 'text-md',
    text: 'Lorem Ipsum',
    color: 'black-md',
    shadow: 'none',
    mb: 3,
    publish: false,
  };

  sanitizeConf = {
    allowedTags: ['br'],
    allowedAttributes: {},
    selfClosing: ['br'],
  };

  handleChange = (e) => {
    this.setState({ html: e.target.value });
  };

  handleBlur = () => {
    const sanitizedHtml = sanitizeHtml(this.state.html, this.sanitizeConf);
    this.setState({ html: sanitizedHtml });
    this.props.updateNode(this.props.address, { text: sanitizedHtml });
  }

  onColorChange = (color) => {
    this.props.updateNode(this.props.address, { color });
  }

  makePill = (c) => {
    let color = null;
    if (c in this.props.colorScheme) {
      color = this.props.colorScheme[c];
    }
    else {
      color = extraColors[c];
    }

    return (
      <span
        onClick={() => this.onColorChange(c)}
        className={cx(
          'bgpill',
          { selected: (c === this.props.color) }
        )}
        style={{ backgroundColor: color }}
      />
    );
  };

  onSizeChange = (e) => {
    const size = e.target.value;
    this.props.updateNode(this.props.address, { size });
  }

  onShadowChange = (e) => {
    const shadow = e.target.value;
    this.props.updateNode(this.props.address, { shadow });
  }

  onMbChange = (value) => {
    const mb = value;
    // console.log('value updated to', mb);
    this.props.updateNode(this.props.address, { mb });
  }


  confirmButtonRemoval = () => {
    this.popoverRef.current.dismiss();
    Modal.confirm({
      title: 'Are you sure?',
      content: null,
      okText: 'Remove',
      okType: 'danger',
      cancelText: 'Never mind',
      onOk: () => {
        this.props.deleteElement(this.props.address);
      },
      onCancel() {},
    });
  }

  popoverContent = () => (
    <React.Fragment>
      <div className="label">
        <div className="main">Size</div>
      </div>
      <RadioGroup defaultValue={this.props.size} size="small" buttonStyle="solid" onChange={this.onSizeChange}>
        <div className="my-2">
          <div className="labelCol pr-2">Header</div>
          <div className="detailCol">
            <RadioButton value="header-h1">H1</RadioButton>
            <RadioButton value="header-h2">H2</RadioButton>
            <RadioButton value="header-h3">H3</RadioButton>
            <RadioButton value="header-h4">H4</RadioButton>
          </div>
        </div>
        <div className="my-2">
          <div className="labelCol pr-2">Subheader</div>
          <div className="detailCol">
            <RadioButton value="subheader-lg">large</RadioButton>
            <RadioButton value="subheader-md">medium</RadioButton>
            <RadioButton value="subheader-sm">small</RadioButton>
          </div>
        </div>
        <div className="my-2">
          <div className="labelCol pr-2">Text</div>
          <div className="detailCol">
            <RadioButton value="text-lg">large</RadioButton>
            <RadioButton value="text-md">medium</RadioButton>
            <RadioButton value="text-sm">small</RadioButton>
          </div>
        </div>
      </RadioGroup>

      <div className="divider" />

      <div className="label">
        <div className="main">Color</div>
      </div>
      <div className="my-2">
        <div className="labelCol pr-2">Grayscale</div>
        <div className="detailCol">
          <div className="bgpillContainer">
            {this.makePill('black-hi')}
            {this.makePill('black-md')}
            {this.makePill('black-lo')}
            {this.makePill('white-hi')}
            {this.makePill('white-md')}
            {this.makePill('white-lo')}
          </div>
        </div>
      </div>
      <div className="my-2">
        <div className="labelCol pr-2">Theme</div>
        <div className="detailCol">
          <div className="bgpillContainer">
            {this.makePill('primary')}
            {this.makePill('accent1')}
            {this.makePill('accent2')}
            {this.makePill('accent3')}
          </div>
        </div>
      </div>

      <div className="divider" />

      <div className="label">
        <div className="main">Shadow under text</div>
        <div className="help">For added contrast against background.</div>
      </div>
      <Radio.Group defaultValue={this.props.shadow} size="small" buttonStyle="solid" onChange={this.onShadowChange}>
        <Radio.Button value="none">None</Radio.Button>
        <Radio.Button value="soft">Soft</Radio.Button>
        <Radio.Button value="hard">Hard</Radio.Button>
      </Radio.Group>

      <div className="divider" />

      <div className="label">
        <div className="main">Spacing below</div>
        <div className="help">Adds a bottom margin.</div>
      </div>

      <Slider
        min={1} max={5} defaultValue={this.props.mb} step={null}
        marks={{ 1: "XS", 2: "S", 3: "M", 4: "L", 5: "XL" }}
        onChange={this.onMbChange}
      />

      <div className="divider" />

      <Button
        type="danger" block icon={<DeleteOutlined />} className="mt-3"
        onClick={this.confirmButtonRemoval}
      >
        Remove this text
      </Button>

      <div className="divider" />

    </React.Fragment>
  );

  onMouseOver = (e) => {
    this.setState({ showBtn: true });
    e.stopPropagation();
  };

  onMouseOut = (e) => {
    this.setState({ showBtn: false });
    e.stopPropagation();
  };

  getSolidColor = (c) => {
    const { colorScheme } = this.props;
    const mycolor = (typeof c === 'undefined') ? 'primary' : c;
    if (mycolor in colorScheme) {
      return colorScheme[mycolor];
    }
    return extraColors[mycolor];
  };

  getContrastShadowColor = (c) => (
    isLight(c) ? '#00000030' : '#ffffff50'
  );

  render() {
    const { color, size, shadow, publish, fontScheme, mb }  = this.props;

    let fontFamily = null;
    let fontWeight = null;
    if (size.startsWith('header-')) {
      [fontFamily] = fontScheme.family;
      [fontWeight] = fontScheme.weight;
    }
    else if (size.startsWith('subheader-')) {
      [, fontFamily] = fontScheme.family;
      [, fontWeight] = fontScheme.weight;
    }
    else {
      [, , fontFamily] = fontScheme.family;
      [, , fontWeight] = fontScheme.weight;
    }

    let textShadow = null;
    if (shadow === 'hard' || shadow === 'soft') {
      const shadowColor = this.getContrastShadowColor(this.getSolidColor(color));
      const shadowOffsetX = 0.05;
      const shadowOffsetY = 1.5 * shadowOffsetX;
      const shadowBlur = (shadow === 'soft') ? shadowOffsetY : 0;
      textShadow = `${shadowOffsetX}em ${shadowOffsetY}em ${shadowBlur}em ${shadowColor}`;
    }

    const styleInEditor = publish ? {} : {
      color: this.getSolidColor(color),
      fontFamily: `${fontFamily}`,
      fontWeight: `${fontWeight}`,
      textShadow,
    };

    let tag = 'div';
    switch (size) {
      case 'header-h1':
        tag = 'h1';
        break;
      case 'header-h2':
        tag = 'h2';
        break;
      case 'header-h3':
        tag = 'h3';
        break;
      case 'header-h4':
        tag = 'h4';
        break;
      default:
        tag = 'div';
        break;
    }

    return (
      <div
        className={cx(
          'ptext',
          this.props.className,
        )}
        onMouseOver={this.onMouseOver}
        onFocus={this.onMouseOver}
        onMouseOut={this.onMouseOut}
        onBlur={this.onMouseOut}
      >

        <ContentEditable
          html={this.state.html}
          className={cx(
            `size-${size}`,
            `color-${color}`,
            `shadow-${shadow}`,
            `mb-${mb}`,
            { editable: publish === false },
          )}
          style={{
            ...this.props.style,
            ...styleInEditor,
          }}
          disabled={publish} // use true to disable edit-ability
          onChange={this.handleChange} // handle innerHTML change
          onBlur={this.handleBlur}
          tagName={tag} // Use a custom HTML tag (uses a div by default)
        />

        <EditorElementPopover
          ref={this.popoverRef}
          usecase="text"
          showBtn={this.state.showBtn}
          btnLabel="text"
          title="Text appearance"
          content={this.popoverContent()}
          placement="bottomLeft"
          publish={publish}
        />

      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./scripts/components/editor/elements/PText.jsx